import type { Query, QueryClient, QueryKey } from '@tanstack/react-query';
import type { ActiveScope } from '@trinity/chat/api';

/**
 * First element of the query keys that survive a change of the active brand/application scope.
 *
 * Everything NOT listed here is reset when the scope changes (default-deny): forgetting to list a
 * scope-independent key only costs a refetch, whereas listing a scope-dependent one would serve one
 * scope's data under another. When unsure, do not list a key.
 */
export const SCOPE_INDEPENDENT_QUERY_KEYS: readonly string[] = [
  // The user's brands list: the same for every scope, and it is what produces the active brand.
  'get:user-brands',
  // Applications per brand: it is what produces the active application and the membership check below,
  // so resetting it would empty the application context and re-select the default application.
  'get:user-applications',
  // Chat session state of the embedded widget/legacy app: it produces the active brand and application
  // (keyed by application already), and resetting it would unmount the whole chat tree.
  'chat-session-state',
  // Session validity polling: depends on the session cookie only.
  'valid-session-checker',
  // App theming: resolved from the user's group, not from a brand or application.
  'get:app-chat/theming',
  // Group branding: resolved from the user's group, not from a brand or application.
  'get:groups/branding',
  // Rate limiting: counts the user's chat queries across all chats and reads group-level features.
  'get:users/rate-limiting',
];

type ApplicationsByBrand = Readonly<Record<string, ReadonlyArray<{ identifier: string }>>>;

export type ScopeApplicationInput = {
  brandIdentifier: string | null | undefined;
  applicationIdentifier: string | null | undefined;
  /**
   * The user's applications keyed by brand identifier. When given, the application must belong to the
   * brand. Pass `null` where that map is not available to skip the membership check.
   */
  applicationsByBrand: ApplicationsByBrand | null;
  /** Key of the previously applied (applicable) scope, `null` when none was applied yet. */
  previousScopeKey: string | null;
};

export type ScopeApplication =
  | { applicable: false }
  | {
      applicable: true;
      scope: ActiveScope;
      scopeKey: string;
      /** True when a different applicable scope was applied before, so scoped cache entries are stale. */
      resetCache: boolean;
    };

export function getScopeKey(scope: ActiveScope): string {
  return `${scope.brandIdentifier}|${scope.applicationIdentifier}`;
}

/**
 * Decides what to do with the brand/application pair that the contexts currently hold.
 *
 * A pair is applicable only when both identifiers are set and the application belongs to the brand:
 * when the user switches brand, the application follows one render (or one request) later, so for a
 * moment the pair is (new brand, old application) and must not be applied.
 */
export function planScopeApplication(input: ScopeApplicationInput): ScopeApplication {
  const { brandIdentifier, applicationIdentifier, applicationsByBrand, previousScopeKey } = input;

  if (!brandIdentifier || !applicationIdentifier) return { applicable: false };

  if (applicationsByBrand) {
    const brandApplications = applicationsByBrand[brandIdentifier];
    if (!brandApplications?.some((application) => application.identifier === applicationIdentifier)) {
      return { applicable: false };
    }
  }

  const scope: ActiveScope = { brandIdentifier, applicationIdentifier };
  const scopeKey = getScopeKey(scope);

  return {
    applicable: true,
    scope,
    scopeKey,
    resetCache: previousScopeKey !== null && previousScopeKey !== scopeKey,
  };
}

export function isScopeIndependentQueryKey(queryKey: QueryKey, scopeIndependentKeys: readonly string[]): boolean {
  const [first] = queryKey;
  return typeof first === 'string' && scopeIndependentKeys.includes(first);
}

/** Matches every query that is NOT scope independent (default-deny). */
export function createScopedQueryPredicate(scopeIndependentKeys: readonly string[]): (query: Query) => boolean {
  return (query) => !isScopeIndependentQueryKey(query.queryKey, scopeIndependentKeys);
}

/**
 * Cancels in-flight scoped queries, then resets them. Cancelling first stops a response that was
 * requested under the old scope from being written into the cache after the reset. `resetQueries`
 * (not `invalidateQueries`) drops the data, so the previous scope's data is never rendered while the
 * refetch for the new scope is running; active queries refetch right away.
 */
export function resetScopedQueries(queryClient: QueryClient, scopeIndependentKeys: readonly string[]): Promise<void> {
  const predicate = createScopedQueryPredicate(scopeIndependentKeys);
  const cancelled = queryClient.cancelQueries({ predicate });
  const reset = queryClient.resetQueries({ predicate });
  return Promise.all([cancelled, reset]).then(() => undefined);
}
